import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart } from '../../../../types/Chart/Chart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import { NavForward } from '../../../../components/buttons/NavForward';
import { FormInput } from '../../../../components/forms/FormInput';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { IsSigningObject } from '../../../../types/Signatures/IsSigningObject';
import { SignatureArea } from '../../../../components/SignatureArea';
import { FileTypes } from '../../../../types/Files/FileTypes';
import { NextSection } from '../../../../components/buttons/NextSection';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';

export const Route = createFileRoute('/_authenticated/$patientId/payment/aob')({
  component: AOBPage,
});

function AOBPage() {
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientInfoQuery = usePatientDoc(patientId);
  const patientInfo = patientInfoQuery.data as PatientDoc;
  const { mutate } = useUpdateChartSection();
  const [isSigning, setIsSigning] = React.useState({
    patientFinal: false,
    witness1: false,
    witness2: false,
    hipaa: false,
    consent: false,
    forms: false,
    abnMedicare: false,
    abn: false,
    aob1: false,
    aob2: false,
  } as IsSigningObject);
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);

  // WRITE FORM DATA TO FIRESTORE
  const handleAOBUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          ASSIGNMENT_OF_BENEFITS: {
            reason: aobForm.state.values.reason,
            signature1DataURI: chart?.ASSIGNMENT_OF_BENEFITS?.signature1DataURI
              ? chart.ASSIGNMENT_OF_BENEFITS.signature1DataURI
              : '',
            signature2DataURI: chart?.ASSIGNMENT_OF_BENEFITS?.signature2DataURI
              ? chart.ASSIGNMENT_OF_BENEFITS.signature2DataURI
              : '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error); // CAPTURE THE ERROR
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // INITIALIZE EMPTY FIRESTORE DOC IF IT DOESN'T EXIST
  React.useEffect(() => {
    const docRef = chart?.ASSIGNMENT_OF_BENEFITS;
    if (chart && !docRef) {
      handleAOBUpdate();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);

  // INITIALIZE FORM
  const aobForm = useForm({
    defaultValues: {
      reason: chart?.ASSIGNMENT_OF_BENEFITS?.reason ? chart.ASSIGNMENT_OF_BENEFITS.reason : '',
      signature1DataURI: chart?.ASSIGNMENT_OF_BENEFITS?.signature1DataURI
        ? chart.ASSIGNMENT_OF_BENEFITS.signature1DataURI
        : '',
      signature2DataURI: chart?.ASSIGNMENT_OF_BENEFITS?.signature2DataURI
        ? chart.ASSIGNMENT_OF_BENEFITS.signature2DataURI
        : '',
    },
    onSubmit: async () => {
      await handleAOBUpdate();
    },
  });

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'aob', patientInfo, value: true });
    }
  }, [patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'aob',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  // NAVIGATION: USE FULLY QUALIFIED PATHS TO KEEP TYPESCRIPT HAPPY IN LINE 25

  if (chartQuery.isLoading || patientInfoQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Attempting to load current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.ASSIGNMENT_OF_BENEFITS) {
    return (
      <>
        {/* <Header patientId={patientId} /> */}
        <div id="medicare-abn-page" className="flex flex-col items-center justify-center">
          <Heading id="medicare-abn-page-header" textContent="Assignment of Benefits (AOB)" />
          <div id="medicare-abn-page-main" className="flex w-5/6 flex-row justify-around">
            <div className="flex flex-col items-start">
              <div className="my-4">
                <p className="text-2xl font-bold">
                  Statement to Permit Payment of Insurance/Medicare Benefits to Provider, Physicians and Patient.
                </p>
                <p id="statement" className="text-xl">
                  I request payment of authorized Insurance/Medicare benefits to me or on my behalf, for any service(s)
                  furnished to me by MIDEO Health, including physician services. I authorize any holder of medical and
                  other information about me to be released to my insurance company/Medicare and its agents for any
                  information needed to determine these benefits or benefits for related services. I understand I am
                  responsible and will pay MIDEO Health for any health insurance deductibles, co-insurance(s)/co-pays,
                  or services not covered by my insurance/Medicare program as defined by the Social Security Act.
                </p>
              </div>
              <div className="my-4">
                <p className="text-2xl font-bold">Assignment of Insurance/Medicare Benefits</p>
                <p id="assignment" className="text-xl">
                  I hereby assign to, and authorize that payment be made directly to MIDEO Health for all benefits
                  otherwise payable to me under the terms of my insurance policies (including Major Medical policies),
                  by reason of the service(s) described in statements rendered by MIDEO Health. MIDEO Health shall
                  refund any payments in excess of its full regular charge(s) to the person(s) entitled to receive the
                  same benefits. I understand that I am financially responsible for all charges not covered, or unpaid
                  by my insurance within a reasonable time.
                </p>
              </div>
              <div className="my-4">
                <p className="text-xl font-bold">SIGNATURE OF BENEFICIARY:</p>
                <SignatureArea
                  elementId={'aob-beneficiary-sig'}
                  type={FileTypes.aobSig1}
                  data={chart}
                  patientId={patientId}
                  isSigning={isSigning}
                  cloudStoragePath={`/user/${patientId}/signatures/ASSIGNMENT_OF_BENEFITS_Beneficiary_Signature`}
                  setIsSigning={setIsSigning}
                  docToUpdate={'ASSIGNMENT_OF_BENEFITS'}
                  sigName="aob1"
                  canComeFromClone={true}
                />
              </div>
              <div className="my-4">
                <p className="text-xl font-bold">SECONDARY SIGNATURE:</p>
                <SignatureArea
                  elementId={'aob-other-sig'}
                  type={FileTypes.aobSig2}
                  data={chart}
                  patientId={patientId}
                  isSigning={isSigning}
                  cloudStoragePath={`/user/${patientId}/signatures/ASSIGNMENT_OF_BENEFITS_Other_Signature`}
                  setIsSigning={setIsSigning}
                  docToUpdate={'ASSIGNMENT_OF_BENEFITS'}
                  sigName="aob2"
                  canComeFromClone={false}
                />
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  aobForm.handleSubmit();
                }}
              >
                <div className="my-4 flex flex-row">
                  <aobForm.Field
                    name="reason"
                    children={(field) => (
                      <>
                        <FormInput
                          label="Reason for Second Signature: "
                          id="reason-for-second-signature"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={false}
                        />
                      </>
                    )}
                  />
                </div>
              </form>
              <Button
                id="submit-aob-info"
                type="submit"
                textContent="Submit"
                onClick={handleAOBUpdate}
                disabled={!chart.ASSIGNMENT_OF_BENEFITS.signature1DataURI}
              />
            </div>
          </div>
          <div id="nav" className="flex flex-row">
            {/* {prevPage && <NavBack to={prevPage} />} */}
            {patientInfo.navType.base === 'self' && nextPageURL && (
              <NavForward to={nextPageURL} disabled={!chart?.ASSIGNMENT_OF_BENEFITS?.signature1DataURI} />
            )}
            {patientInfo?.conciergeNavigator?.photosUnlocked && nextPageURL && <NextSection to={nextPageURL} />}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
